import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useAuth } from '../../contexts/AuthContext';
import { format } from 'date-fns';
import { id } from 'date-fns/locale';

const {
  FiUsers,
  FiUserPlus,
  FiEdit,
  FiTrash2,
  FiCheck,
  FiX,
  FiAlertCircle,
  FiRefreshCw,
  FiEye,
  FiEyeOff,
  FiUser,
  FiUserCheck,
  FiLock
} = FiIcons;

const UserManagement = () => {
  const { user, getUsers, createUser, updateUser, toggleUserStatus } = useAuth();
  
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  
  const [currentUser, setCurrentUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    fullName: '',
    role: 'viewer'
  });
  
  // Fetch users on component mount
  useEffect(() => {
    fetchUsers();
  }, []);
  
  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    
    try {
      if (!user || user.role !== 'admin') {
        throw new Error('Unauthorized access');
      }
      
      const usersData = await getUsers();
      setUsers(usersData);
    } catch (error) {
      console.error('Error fetching users:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const resetForm = () => {
    setFormData({
      username: '',
      password: '',
      fullName: '',
      role: 'viewer'
    });
  };
  
  const handleAddUser = async (e) => {
    e.preventDefault();
    
    try {
      await createUser(formData);
      fetchUsers();
      setShowAddModal(false);
      resetForm();
    } catch (error) {
      console.error('Error adding user:', error);
      setError(error.message);
    }
  };
  
  const handleEditUser = (user) => {
    setCurrentUser(user);
    setFormData({
      username: user.username,
      password: '',
      fullName: user.full_name || '',
      role: user.role
    });
    setShowEditModal(true);
  };
  
  const handleUpdateUser = async (e) => {
    e.preventDefault();
    
    try {
      const updates = {
        full_name: formData.fullName,
        role: formData.role
      };
      
      if (formData.password) {
        updates.password = formData.password;
      }
      
      await updateUser(currentUser.id, updates);
      fetchUsers();
      setShowEditModal(false);
    } catch (error) {
      console.error('Error updating user:', error);
      setError(error.message);
    }
  };
  
  const handleDeleteUser = (user) => {
    setCurrentUser(user);
    setShowDeleteModal(true);
  };
  
  const confirmDeleteUser = async () => {
    try {
      await toggleUserStatus(currentUser.id, false);
      fetchUsers();
      setShowDeleteModal(false);
    } catch (error) {
      console.error('Error deactivating user:', error);
      setError(error.message);
    }
  };
  
  const handleReactivateUser = async (userId) => {
    try {
      await toggleUserStatus(userId, true);
      fetchUsers();
    } catch (error) {
      console.error('Error reactivating user:', error);
      setError(error.message);
    }
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    try {
      return format(new Date(dateString), 'dd/MM/yyyy HH:mm', { locale: id });
    } catch {
      return dateString;
    }
  };
  
  if (!user || user.role !== 'admin') {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="p-6 text-center">
            <div className="bg-red-50 p-6 rounded-xl border border-red-200 inline-block">
              <SafeIcon icon={FiAlertCircle} className="text-red-500 text-4xl mx-auto mb-3" />
              <h3 className="text-lg font-medium text-red-800 mb-2">Akses Ditolak</h3>
              <p className="text-red-700">Anda tidak memiliki izin untuk mengakses halaman ini.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="container mx-auto px-4 py-6">
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="bg-gradient-to-r from-teal-600 to-emerald-600 p-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between">
            <div className="flex items-center space-x-3 mb-4 md:mb-0">
              <SafeIcon icon={FiUsers} className="text-white text-2xl" />
              <h1 className="text-xl md:text-2xl font-bold text-white">User Management</h1>
            </div>
            
            <div className="flex space-x-2">
              <button
                onClick={fetchUsers}
                className="bg-white/20 hover:bg-white/30 text-white px-3 py-2 rounded-lg transition-colors flex items-center space-x-1"
              >
                <SafeIcon icon={FiRefreshCw} className="text-sm" />
                <span>Refresh</span>
              </button>
              
              <button
                onClick={() => {
                  resetForm();
                  setShowAddModal(true);
                }}
                className="bg-white text-teal-700 hover:bg-teal-50 px-3 py-2 rounded-lg transition-colors flex items-center space-x-1"
              >
                <SafeIcon icon={FiUserPlus} className="text-sm" />
                <span>Add User</span>
              </button>
            </div>
          </div>
        </div>
        
        {error && (
          <div className="p-4 bg-red-50 border-b border-red-200">
            <div className="flex items-center space-x-2 text-red-700">
              <SafeIcon icon={FiAlertCircle} />
              <span>{error}</span>
            </div>
          </div>
        )}
        
        <div className="p-6">
          {loading ? (
            <div className="flex justify-center p-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
            </div>
          ) : users.length === 0 ? (
            <div className="text-center p-8 bg-gray-50 rounded-lg">
              <SafeIcon icon={FiUsers} className="text-gray-400 text-4xl mx-auto mb-2" />
              <p className="text-gray-500">No users found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-3 border-b border-gray-200 text-sm font-medium text-gray-700">Username</th>
                    <th className="px-4 py-3 border-b border-gray-200 text-sm font-medium text-gray-700">Full Name</th>
                    <th className="px-4 py-3 border-b border-gray-200 text-sm font-medium text-gray-700">Role</th>
                    <th className="px-4 py-3 border-b border-gray-200 text-sm font-medium text-gray-700">Status</th>
                    <th className="px-4 py-3 border-b border-gray-200 text-sm font-medium text-gray-700">Created</th>
                    <th className="px-4 py-3 border-b border-gray-200 text-sm font-medium text-gray-700">Last Login</th>
                    <th className="px-4 py-3 border-b border-gray-200 text-sm font-medium text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr
                      key={user.id}
                      className={`hover:bg-gray-50 ${!user.is_active ? 'bg-gray-50 text-gray-400' : ''}`}
                    >
                      <td className="px-4 py-3 border-b border-gray-200">
                        <div className="flex items-center space-x-2">
                          <SafeIcon icon={FiUser} className="text-gray-400" />
                          <span>{user.username}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 border-b border-gray-200">
                        {user.full_name || '-'}
                      </td>
                      <td className="px-4 py-3 border-b border-gray-200">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          user.role === 'admin'
                            ? 'bg-purple-100 text-purple-800'
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {user.role}
                        </span>
                      </td>
                      <td className="px-4 py-3 border-b border-gray-200">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          user.is_active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {user.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3 border-b border-gray-200 text-sm text-gray-500">
                        {formatDate(user.created_at)}
                      </td>
                      <td className="px-4 py-3 border-b border-gray-200 text-sm text-gray-500">
                        {formatDate(user.last_login)}
                      </td>
                      <td className="px-4 py-3 border-b border-gray-200">
                        <div className="flex items-center space-x-2">
                          {user.is_active ? (
                            <>
                              <button
                                onClick={() => handleEditUser(user)}
                                className="p-1 text-blue-600 hover:text-blue-800"
                                title="Edit User"
                              >
                                <SafeIcon icon={FiEdit} />
                              </button>
                              <button
                                onClick={() => handleDeleteUser(user)}
                                className="p-1 text-red-600 hover:text-red-800"
                                title="Deactivate User"
                              >
                                <SafeIcon icon={FiTrash2} />
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={() => handleReactivateUser(user.id)}
                              className="p-1 text-green-600 hover:text-green-800"
                              title="Reactivate User"
                            >
                              <SafeIcon icon={FiUserCheck} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      
      {/* Add User Modal */}
      <AnimatePresence>
        {showAddModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl max-w-md w-full"
            >
              <div className="bg-teal-600 text-white p-4 rounded-t-xl flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <SafeIcon icon={FiUserPlus} />
                  <h3 className="text-lg font-semibold">Add New User</h3>
                </div>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="text-white hover:text-teal-100"
                >
                  <SafeIcon icon={FiX} />
                </button>
              </div>
              
              <form onSubmit={handleAddUser} className="p-6 space-y-4">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Username</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <SafeIcon icon={FiUser} className="text-gray-400" />
                    </div>
                    <input
                      type="text"
                      name="username"
                      value={formData.username}
                      onChange={handleInputChange}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                      placeholder="Enter username"
                      required
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Password</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <SafeIcon icon={FiLock} className="text-gray-400" />
                    </div>
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                      placeholder="Enter password"
                      required
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Full Name</label>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    placeholder="Enter full name"
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Role</label>
                  <select
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  >
                    <option value="viewer">Viewer</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                
                <div className="flex justify-end space-x-2 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
                  >
                    Add User
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Edit User Modal */}
      <AnimatePresence>
        {showEditModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl max-w-md w-full"
            >
              <div className="bg-blue-600 text-white p-4 rounded-t-xl flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <SafeIcon icon={FiEdit} />
                  <h3 className="text-lg font-semibold">Edit User</h3>
                </div>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="text-white hover:text-blue-100"
                >
                  <SafeIcon icon={FiX} />
                </button>
              </div>
              
              <form onSubmit={handleUpdateUser} className="p-6 space-y-4">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Username</label>
                  <input
                    type="text"
                    value={formData.username}
                    className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-500"
                    disabled
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Password</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <SafeIcon icon={FiLock} className="text-gray-400" />
                    </div>
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Leave blank to keep current password"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Full Name</label>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter full name"
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Role</label>
                  <select
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="viewer">Viewer</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                
                <div className="flex justify-end space-x-2 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Update User
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Delete User Modal */}
      <AnimatePresence>
        {showDeleteModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl max-w-md w-full"
            >
              <div className="bg-red-600 text-white p-4 rounded-t-xl">
                <div className="flex items-center space-x-2">
                  <SafeIcon icon={FiAlertCircle} />
                  <h3 className="text-lg font-semibold">Deactivate User</h3>
                </div>
              </div>
              
              <div className="p-6">
                <p className="mb-4">
                  Are you sure you want to deactivate user <strong>{currentUser?.username}</strong>?
                </p>
                <p className="text-gray-600 mb-6">
                  The user will no longer be able to log in, but their data will be preserved.
                </p>
                
                <div className="flex justify-end space-x-2">
                  <button
                    onClick={() => setShowDeleteModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmDeleteUser}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                  >
                    Deactivate
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default UserManagement;